import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { SSOAuthResponse, SSOProvider, SSOConfig, isSsoProviderEnabled } from '../interfaces/sso.interface';
import { GoogleSsoService } from './google-sso.service';
import { AppleSsoService } from './apple-sso.service';
import { AppConfigService } from '@/app/core/services/app-config.service';

@Injectable({
  providedIn: 'root',
})
export class UnifiedSsoService {
  private ssoServices: Map<SSOProvider, { initialize: (config: SSOConfig) => Promise<boolean>; authenticate: () => Observable<SSOAuthResponse>; isAvailable: () => boolean; clearSession?: () => void }> = new Map();
  private initializedProviders: Set<SSOProvider> = new Set();

  constructor(
    private googleSsoService: GoogleSsoService,
    private appleSsoService: AppleSsoService,
    private appConfigService: AppConfigService
  ) {
    this.ssoServices.set(SSOProvider.GOOGLE, googleSsoService);
    this.ssoServices.set(SSOProvider.APPLE, appleSsoService);
  }

  /**
   * Initialize all available SSO providers
   */
  async initializeAllProviders(): Promise<void> {
    const initPromises: Promise<void>[] = [];

    if (this.appConfigService.googleConfig?.web?.clientId) {
      console.log('Google SSO: Found configuration, initializing...', {
        clientId: this.appConfigService.googleConfig.web.clientId,
      });
      initPromises.push(
        this.initializeProvider(SSOProvider.GOOGLE, {
          clientId: this.appConfigService.googleConfig.web.clientId,
        })
      );
    } else {
      console.warn('Google SSO: No configuration found. Check appConfigService.googleConfig.web.clientId');
    }

    if (this.appConfigService.appleConfig?.web?.ssoIdentifierId) {
      console.log('Apple SSO: Found configuration, initializing...', {
        clientId: this.appConfigService.appleConfig.web.ssoIdentifierId,
      });
      initPromises.push(
        this.initializeProvider(SSOProvider.APPLE, {
          clientId: this.appConfigService.appleConfig.web.ssoIdentifierId,
        })
      );
    } else {
      console.warn('Apple SSO: No configuration found. Check appConfigService.appleConfig.web.clientId');
    }

    await Promise.allSettled(initPromises);
  }

  /**
   * Initialize a specific SSO provider
   */
  async initializeProvider(provider: SSOProvider, config: SSOConfig): Promise<void> {
    if (!isSsoProviderEnabled(provider)) {
      console.warn(`SSO provider ${provider} is disabled`);
      return;
    }

    try {
      console.log(`SSO: Initializing provider ${provider} with config:`, config);

      const service = this.ssoServices.get(provider);
      if (!service) {
        throw new Error(`SSO service not found for provider: ${provider}`);
      }

      const success = await service.initialize(config);
      if (success) {
        this.initializedProviders.add(provider);
        console.log(`SSO provider ${provider} initialized successfully`);
      } else {
        console.warn(`Failed to initialize SSO provider: ${provider}`);
      }
    } catch (error) {
      console.error(`Error initializing SSO provider ${provider}:`, error);
    }
  }

  /**
   * Authenticate with a specific SSO provider
   */
  authenticate(provider: SSOProvider): Observable<SSOAuthResponse> {
    if (!isSsoProviderEnabled(provider)) {
      return of({
        success: false,
        error: `SSO provider ${provider} is disabled`,
        errorCode: 'PROVIDER_DISABLED',
      });
    }

    const service = this.ssoServices.get(provider);

    if (!service) {
      return of({
        success: false,
        error: `SSO service not found for provider: ${provider}`,
        errorCode: 'PROVIDER_NOT_FOUND',
      });
    }

    if (!this.initializedProviders.has(provider)) {
      return of({
        success: false,
        error: `SSO provider ${provider} not initialized`,
        errorCode: 'PROVIDER_NOT_INITIALIZED',
      });
    }

    return service.authenticate();
  }

  /**
   * Check if a provider is available and initialized
   */
  isProviderAvailable(provider: SSOProvider): boolean {
    if (!isSsoProviderEnabled(provider)) {
      return false;
    }

    const service = this.ssoServices.get(provider);
    return !!service && service.isAvailable() && this.initializedProviders.has(provider);
  }

  /**
   * Get all available providers
   */
  getAvailableProviders(): SSOProvider[] {
    return Array.from(this.initializedProviders).filter((provider) => this.isProviderAvailable(provider));
  }

  /**
   * Get provider configuration
   */
  getProviderConfig(provider: SSOProvider): SSOConfig | null {
    if (!isSsoProviderEnabled(provider)) {
      return null;
    }

    switch (provider) {
      case SSOProvider.GOOGLE:
        return this.appConfigService.googleConfig?.web?.clientId
          ? {
              clientId: this.appConfigService.googleConfig.web.clientId,
            }
          : null;

      case SSOProvider.APPLE:
        return this.appConfigService.appleConfig?.web?.ssoIdentifierId
          ? {
              identifierId: this.appConfigService.appleConfig.web.ssoIdentifierId,
            }
          : null;

      default:
        return null;
    }
  }

  /**
   * Get initialization status for all providers
   */
  getInitializationStatus(): Record<SSOProvider, boolean> {
    const status: Record<SSOProvider, boolean> = {} as Record<SSOProvider, boolean>;

    Object.values(SSOProvider).forEach((provider) => {
      status[provider] = this.initializedProviders.has(provider);
    });

    return status;
  }

  /**
   * Clear all SSO sessions (useful for logout)
   */
  clearAllSessions(): void {
    this.ssoServices.forEach((service) => {
      if (service.clearSession) {
        service.clearSession();
      }
    });
  }
}
